import React, { Fragment, useEffect, useState } from 'react';
import { MdAccountCircle, MdClose, MdLink, MdLinkOff, MdMenu } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import {
  AppBar as MuiAppBar,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Drawer,
  Icon,
  IconButton,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { PropTypes } from 'prop-types';

import { useInterval } from '../../components/auxiliary/useInterval.js';
import { MConfirmationBox } from '../../components/ui/MConfirmationBox.js';
import { useGlobalContext } from '../../context/AppContext.js';
import { doctorDataApiCall, pryvApiCall } from '../../data/apiCalls.js';
import { appBarTheme } from '../../styles/appBarTheme.js';

import { MAuthentication } from './Authentication/MAuthentication.js';
import { MAccountInfoDialog } from './MAccountInfoDialog';
import { MCopyright } from './MCopyright';
import { MMenuList } from './MMenuList';

const DRAWER_WIDTH = 240;
const TOKEN_POLLING_INTERVAL_MS = 2 * 60 * 1000 // Two minutes

const Main = styled('div', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(2),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  marginLeft: `-${DRAWER_WIDTH}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: 0
  })
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  backgroundColor: appBarTheme.color,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    marginLeft: `${DRAWER_WIDTH}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end'
}));

/**
 * Makes the "AppBar" component.
 * @return {jsx} The app bar component.
 */
export const MAppBar = ({ children }) => {
  const { setPatientAccesses, docEndpoint, setDocEndpoint, setDoctorInfoEvents } =
    useGlobalContext();

  const [pollingFequency, setPollingFrequency] = useState(null);
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [isLogoutOpen, setLogoutOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountInfoDialogueOpen, setAccountInfoDialogueOpen] = useState(false);

  const theme = useTheme();
  const history = useHistory();

  useEffect(() => {
    if (!docEndpoint) {
      setPollingFrequency(null);
      return;
    }
    pryvApiCall(docEndpoint, [{
      "method": "getAccessInfo",
      "params": {},
    }])

    sessionStorage.setItem('docEP', docEndpoint);
    establishDbConnection(docEndpoint);
    setPollingFrequency(TOKEN_POLLING_INTERVAL_MS)
  }, [docEndpoint]);

  // Handle manual refresh
  useEffect(() => {
    const storedEndpoint = sessionStorage.getItem('docEP');
    if (storedEndpoint) {
      setDocEndpoint(storedEndpoint);
    }
  }, []);

  useEffect(() => {
    return () => setPollingFrequency(null);
  }, [docEndpoint])


  useInterval(() => {
    const docEndpoint = sessionStorage.getItem('docEP');
    pryvApiCall(docEndpoint, [{
      "method": "getAccessInfo",
      "params": {},
    }])
      .catch((error) => {
        console.error(`Token check failed! Logging out! Reason - ${error}`)
        
        setPatientAccesses([]);
        setDocEndpoint();
        setHasLoaded(false);
        
        const instance = sessionStorage.getItem('dbInstance');
        sessionStorage.clear();
        sessionStorage.setItem('dbInstance', instance);

        setLogoutOpen(false);

        history.push('/')
      })

  }, pollingFequency)

  const dbInstance = window.sessionStorage.getItem('dbInstance');

  // setup marked for comparison in sessionstorage
  if (!sessionStorage.getItem('markedActivities')) {
    sessionStorage.setItem('markedActivities', JSON.stringify([]));
  }

  /**
   * establish connection to the database, which entails fetching the doc's
   info, checking all of the patient accesses. All 'results' are stored in
   corresponding React states.
   * @param {Url} apiEndpoint
   */
  const establishDbConnection = async (apiEndpoint) => {
    if (!apiEndpoint || hasLoaded) {
      return;
    }
    pryvApiCall(apiEndpoint, doctorDataApiCall)
      .then((result) => {
        // Get Doc Data & accesses to patient data
        const usernameEvent = {
          streamIds: ['.username'],
          streamId: '.username',
          content: apiEndpoint.split('@')[1].split('.')[0]
        }

        result[0].events.push(usernameEvent)
        setDoctorInfoEvents(result[0].events);
        return result[1].events;
      })
      // Check if they're still connected to "old"
      // development/staging domains and move if so
      .then((accesses) => {
        setPatientAccesses(accesses);
        setHasLoaded(true);
      })

      .catch((err) => console.error(err));
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const onLogout = async () => {
    const instance = sessionStorage.getItem('dbInstance');
    
    sessionStorage.clear();
    sessionStorage.setItem('dbInstance', instance);
    
    pryvApiCall(
      docEndpoint,
      [{
        method: "getAccessInfo",
        params: {},
      }]
    )
    .then(res => {
      return pryvApiCall(
        docEndpoint,
        [{
          method: "accesses.delete",
          params: {
            id: res.id,
          },
        }]
      )
    })
    .catch(e => {
      console.error(`Failed to invalidate access. Reason :${e}`)
    })

    setPatientAccesses([]);
    setDocEndpoint();
    setHasLoaded(false);

    setLogoutOpen(false);

    // Force to run after state updates.
    setTimeout(() => {
      history.replace('/');
    });
  };

  const onLogoutCancel = () => {
    setLogoutOpen(false);
  };

  const onAuthButtonClick = () => {
    if (docEndpoint) {
      setLogoutOpen(true);
    } else {
      setAuthOpen(true);
    }
  };

  return (
    <Fragment>
      <Box sx={{ display: 'flex', height: '100%' }}>
        <CssBaseline />
        <AppBar sx={{ backgroundColor: appBarTheme.color }} position="fixed" open={drawerOpen}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* icon + Nushu home button */}
            <Box>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{ mr: 2, ...(drawerOpen && { display: 'none' }) }}>
                <MdMenu />
              </IconButton>
              <Button
                id="nushu-button"
                variant="outlined"
                color="secondary"
                onClick={() => history.push('/')}>
                <Typography component="h1" variant="h5">
                  <i>
                    <b>NUSHU</b>
                  </i>
                </Typography>
              </Button>
            </Box>

            {/* DB Instance information */}
            <Box
              sx={{
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                [theme.breakpoints.up('md')]: {
                  display: 'flex'
                }
              }}>
              <Typography
                variant="overline"
                sx={{
                  color: appBarTheme.textColor
                }}>
                {appBarTheme.text}
              </Typography>
              {!['Research', ''].includes(appBarTheme.text) && (
                <Typography
                  variant="overline"
                  sx={{
                    color: appBarTheme.textColor,
                    fontSize: 10,
                    lineHeight: 0.1
                  }}>
                  {dbInstance && `${dbInstance}-DB [${process.env.REACT_APP_CERTIFICATION}]`}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Database Connection">
                <Icon sx={{ marginX: 2 }} color="inherit">
                  {docEndpoint ? (
                    <MdLink style={{ color: theme.palette.success.main }} />
                  ) : hasLoaded ? (
                    <CircularProgress color="secondary" size={24} />
                  ) : (
                    <MdLinkOff style={{ color: theme.palette.error.main }} />
                  )}
                </Icon>
              </Tooltip>

              <Button color="inherit" onClick={onAuthButtonClick} id="login-button">
                {!docEndpoint ? 'Login' : 'Logout'}
              </Button>
              {docEndpoint && hasLoaded && (
                <Fragment>
                  <IconButton onClick={() => setAccountInfoDialogueOpen(true)}>
                    <MdAccountCircle style={{ color: theme.palette.common.white }} />
                  </IconButton>
                  <MAccountInfoDialog
                    isOpen={accountInfoDialogueOpen}
                    onClose={() => setAccountInfoDialogueOpen(false)}
                  />
                </Fragment>
              )}
            </Box>
          </Toolbar>
        </AppBar>
        <Drawer
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box'
            }
          }}
          variant="persistent"
          anchor="left"
          open={drawerOpen}>
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === 'ltr' ? <MdClose /> : <MdMenu />}
            </IconButton>
          </DrawerHeader>
          <MMenuList />

          {drawerOpen && (
            <Box sx={{ position: 'fixed', bottom: 0, width: 'inherit', mb: 5 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  paddingBottom: '2rem',
                  [theme.breakpoints.up('md')]: {
                    display: 'none'
                  }
                }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'black'
                  }}>
                  {appBarTheme.text}
                </Typography>
                {!['Staging Build', ''].includes(appBarTheme.text) && (
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'black',
                      fontSize: 10,
                      lineHeight: 0.1
                    }}>
                    {dbInstance && `${dbInstance}-DB [${process.env.REACT_APP_CERTIFICATION}]`}
                  </Typography>
                )}
              </Box>
              <MCopyright />
            </Box>
          )}
        </Drawer>
        <Main open={drawerOpen}>
          <DrawerHeader />
          {children}
        </Main>
      </Box>
      <MConfirmationBox
        text="Are you sure you want to logout?"
        isOpen={isLogoutOpen}
        onConfirmation={onLogout}
        onCancel={onLogoutCancel}
      />
      <MAuthentication isOpen={isAuthOpen} setOpen={setAuthOpen} />
    </Fragment>
  );
};

MAppBar.propTypes = {
  children: PropTypes.node
};
